import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useTenant } from "@/lib/TenantContext";
import { useT } from "@/lib/LanguageContext";
import ClientAuthPanel from "@/components/auth/ClientAuthPanel";
import * as crmApi from "./api/crmApi";
import { apiErrorCode, crmErrorMessage } from "./api/crmErrors";
import { CRM_ERRORS } from "./contract";
import { crmHref } from "./paths";
import { CRM } from "../../constants/testIds/crm";

/**
 * `/admin/join?token=` (spec "Team", decision 5, P3, lane F5) — the invited helper's
 * accept page, mounted on BOTH admin route trees (routes/adminRoutes.jsx,
 * tenant/TenantSite.jsx) OUTSIDE `ProtectedRoute`: a not-yet-accepted helper owns no
 * tenant, so `ProtectedRoute`'s own `/api/saas/my-tenant` bootstrap would refuse them
 * before this page ever got a chance to run (see CR-F5-1 — that refusal itself needs a
 * backend fix this lane does not own; this page is built to the contract regardless).
 *
 * Flow: no token → dead-link message. Not signed in → the SAME email+OTP panel every
 * client uses (`ClientAuthPanel`, native to any tenant host). Signed in → the token is
 * posted at once (`POST /api/crm/team/accept`); success routes into the CRM dashboard,
 * where `tenant_role` on `/auth/me` now reads "helper". `INVITE_EMAIL_MISMATCH` (signed
 * in with the wrong address) offers "sign in with a different email" (logs out and
 * re-arms this same flow); `INVITE_EXPIRED` has no retry — a fresh invite is the owner's
 * job, not something this page can produce.
 */
export default function JoinPage() {
  const t = useT("crm");
  const tc = useT("common");
  const { user, loading, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";

  const [status, setStatus] = useState("idle"); // idle | accepting | mismatch | expired | error
  const [error, setError] = useState(null);
  // Guards against firing the accept POST twice for the same signed-in identity (React
  // StrictMode double-invokes effects in dev; the token is otherwise single-use).
  const attemptedFor = useRef(null);

  useEffect(() => {
    if (loading || !user || !token) return;
    if (attemptedFor.current === user.email) return;
    attemptedFor.current = user.email;
    setStatus("accepting");
    setError(null);
    crmApi.acceptTeamInvite(token)
      .then(() => { navigate(crmHref(""), { replace: true }); })
      .catch((e) => {
        const code = apiErrorCode(e);
        if (code === CRM_ERRORS.inviteEmailMismatch) setStatus("mismatch");
        else if (code === CRM_ERRORS.inviteExpired) setStatus("expired");
        else { setError(e); setStatus("error"); }
      });
  }, [loading, user, token, navigate]);

  const switchAccount = async () => {
    await logout();
    attemptedFor.current = null;
    setStatus("idle");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-eh-bg px-6">
      <div data-testid={CRM.join} className="max-w-md w-full bg-eh-cream rounded-3xl border border-eh-border-soft p-10 shadow-[0_24px_60px_-25px_rgba(44,53,49,0.2)] text-center">
        <h1 className="font-display text-3xl text-eh-ink leading-tight">
          {t("join.title", { practice: tenant?.name || "" })}
        </h1>
        <p className="text-eh-muted mt-3">{t("join.subtitle")}</p>

        {!token && (
          <p data-testid={CRM.joinNoToken} className="mt-6 text-sm text-red-600">{t("join.noToken")}</p>
        )}

        {token && loading && (
          <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-eh-sage-deep" size={28} /></div>
        )}

        {token && !loading && !user && (
          <div className="mt-8"><ClientAuthPanel clientConsents={false} /></div>
        )}

        {token && !loading && user && (
          <p data-testid={CRM.joinSignedInAs} className="mt-4 text-xs text-eh-muted">
            {t("join.signedInAs", { email: user.email })}
          </p>
        )}

        {token && !loading && user && status === "accepting" && (
          <div data-testid={CRM.joinAccepting} className="mt-6 flex flex-col items-center gap-3">
            <Loader2 className="animate-spin text-eh-sage-deep" size={28} />
            <p className="text-sm text-eh-muted">{t("join.accepting")}</p>
          </div>
        )}

        {token && !loading && user && status === "mismatch" && (
          <div data-testid={CRM.joinMismatch} className="mt-4">
            <p className="text-sm font-medium text-eh-ink">{t("join.mismatchTitle")}</p>
            <p className="mt-2 text-sm text-eh-muted">{t("join.mismatchBody", { email: user.email })}</p>
            <button
              type="button"
              onClick={switchAccount}
              data-testid={CRM.joinMismatchSwitch}
              className="mt-4 inline-flex items-center justify-center gap-2 w-full px-6 py-2.5 rounded-full bg-eh-ink text-eh-cream hover:bg-eh-ink/90 transition text-sm font-medium"
            >
              {t("join.switchAccount")}
            </button>
          </div>
        )}

        {token && !loading && user && status === "expired" && (
          <div data-testid={CRM.joinExpired} className="mt-4">
            <p className="text-sm font-medium text-eh-ink">{t("join.expiredTitle")}</p>
            <p className="mt-2 text-sm text-eh-muted">{t("join.expiredBody")}</p>
          </div>
        )}

        {token && !loading && user && status === "error" && (
          <p data-testid={CRM.joinError} className="mt-4 text-sm text-red-600">
            {crmErrorMessage(error, t, tc, "join.genericError")}
          </p>
        )}
      </div>
    </div>
  );
}
